/**
 * ``useSelectedTrackDash`` — the Profile's ``map.track.selected_dash`` flag
 * and ``map.track.selected_dash_pattern`` choice (REQ-UI-200, amended
 * 2026-10-19), read exactly the way :func:`useTracksLayerVisible` reads
 * ``map.visible_layers.tracks``: straight off :data:`PreferencesContext`,
 * non-throwing.
 *
 * No provider (bare unit tests), a Profile written before the ``track``
 * section existed, or one written between the toggle and the pattern ⇒ the
 * schema defaults: ``enabled: true``, ``pattern: "dotted"``. The setting
 * exists because the operator could not tell which of several neighbouring
 * lines was selected (report 2026-10-09), so absence shows the emphasis
 * rather than hiding it — the same fail-visible stance as the layer flag
 * (CLAUDE.md §8.1). An unknown pattern value (a Profile from a newer UI, or
 * a hand-edited document) falls back to the default rather than to no
 * overlay, for the same reason.
 */
import { useContext } from "react";
import {
  normalizeSelectedDashPattern,
  type SelectedDashPattern,
} from "@/map/selected-dash-pattern";
import { PreferencesContext } from "@/preferences/preferences-context";

/** The two halves of the selected-track overlay setting. */
export interface SelectedTrackDash {
  /** Whether the overlay draws at all (``map.track.selected_dash``). */
  readonly enabled: boolean;
  /** Which pattern it draws (``map.track.selected_dash_pattern``). */
  readonly pattern: SelectedDashPattern;
}

export function useSelectedTrackDash(): SelectedTrackDash {
  const ctx = useContext(PreferencesContext);
  const track = ctx?.workingState.map?.track;
  return {
    enabled: track?.selected_dash ?? true,
    pattern: normalizeSelectedDashPattern(track?.selected_dash_pattern),
  };
}
